import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useCity } from '../contexts/CityContext';
import api from '../services/api';
import { Loading } from '../components/Loading';
import { EmptyState } from '../components/EmptyState';
import { BottomNav } from '../components/BottomNav';
import { ImageUploadField } from '../components/ImageUploadField';

export function AccountPage() {
  const { user, logout, exportData, deleteAccount, updateProfile, updateAvatar, changePassword } = useAuth();
  const [avatarError, setAvatarError] = useState(null);
  // LE-027: edição de nome e telefone
  const [editing, setEditing] = useState(false);
  const [formName, setFormName] = useState('');
  const [formPhone, setFormPhone] = useState('');
  const [savingProfile, setSavingProfile] = useState(false);
  // LE-029: troca de senha (verificação da senha atual no backend)
  const [pwEditing, setPwEditing] = useState(false);
  const [pwCurrent, setPwCurrent] = useState('');
  const [pwNew, setPwNew] = useState('');
  const [pwConfirm, setPwConfirm] = useState('');
  const [savingPw, setSavingPw] = useState(false);
  const [pwError, setPwError] = useState(null);
  const { selectedCity } = useCity();
  const navigate = useNavigate();
  const [sessions, setSessions] = useState([]);
  const [loadingSessions, setLoadingSessions] = useState(true);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [exported, setExported] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get('/auth/sessions');
        setSessions(data.data || []);
      } catch {
        setSessions([]);
      } finally {
        setLoadingSessions(false);
      }
    })();
  }, []);

  const revokeSession = async (id) => {
    setBusy(true);
    try {
      await api.delete(`/auth/sessions/${id}`);
      setSessions(prev => prev.filter(s => s.id !== id));
      setMessage({ type: 'ok', text: 'Sessão encerrada com sucesso.' });
    } catch (err) {
      setMessage({ type: 'err', text: err.response?.data?.error?.message || 'Não foi possível encerrar a sessão.' });
    } finally {
      setBusy(false);
    }
  };

  const handleExport = async () => {
    setBusy(true);
    setMessage(null);
    try {
      const data = await exportData();
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'meus-dados-nossa-cidade.json';
      a.click();
      URL.revokeObjectURL(url);
      setExported(true);
      setMessage({ type: 'ok', text: 'Seus dados foram exportados em formato JSON.' });
    } catch (err) {
      setMessage({ type: 'err', text: err.response?.data?.error?.message || 'Falha ao exportar dados.' });
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async () => {
    setBusy(true);
    try {
      await deleteAccount();
      navigate('/');
    } catch (err) {
      setMessage({ type: 'err', text: err.response?.data?.error?.message || 'Não foi possível excluir a conta.' });
      setBusy(false);
    }
  };

  // LE-027: inicia a edição com os valores atuais
  const startEditing = () => {
    setFormName(user?.name || '');
    setFormPhone(user?.phone || '');
    setEditing(true);
    setMessage(null);
  };

  // LE-027: salva nome/telefone via PATCH /auth/me (só envia campos alterados)
  const handleSaveProfile = async (e) => {
    e.preventDefault();
    const fields = {};
    const trimmedName = formName.trim();
    const trimmedPhone = formPhone.trim();
    if (trimmedName !== (user?.name || '')) fields.name = trimmedName;
    if (trimmedPhone !== (user?.phone || '')) fields.phone = trimmedPhone;
    if (Object.keys(fields).length === 0) {
      setEditing(false);
      setMessage({ type: 'ok', text: 'Nenhuma alteração para salvar.' });
      return;
    }
    setSavingProfile(true);
    setMessage(null);
    try {
      await updateProfile(fields);
      setEditing(false);
      setMessage({ type: 'ok', text: 'Dados do perfil atualizados.' });
    } catch (err) {
      setMessage({ type: 'err', text: err.response?.data?.error?.message || 'Não foi possível salvar os dados.' });
    } finally {
      setSavingProfile(false);
    }
  };

  // LE-029: inicia o formulário de troca de senha
  const startPwEditing = () => {
    setPwCurrent('');
    setPwNew('');
    setPwConfirm('');
    setPwError(null);
    setPwEditing(true);
    setMessage(null);
  };

  // LE-029: troca a senha (validação client de tamanho/confirmação; a prova de
  // posse — senha atual — é verificada no backend com bcrypt)
  const handleChangePassword = async (e) => {
    e.preventDefault();
    setPwError(null);
    if (pwNew.length < 8) {
      setPwError('A nova senha deve ter pelo menos 8 caracteres.');
      return;
    }
    if (pwNew !== pwConfirm) {
      setPwError('A confirmação não confere com a nova senha.');
      return;
    }
    if (pwNew === pwCurrent) {
      setPwError('A nova senha deve ser diferente da atual.');
      return;
    }
    setSavingPw(true);
    try {
      await changePassword({ currentPassword: pwCurrent, newPassword: pwNew });
      setPwEditing(false);
      setPwCurrent('');
      setPwNew('');
      setPwConfirm('');
      setMessage({ type: 'ok', text: 'Senha alterada com sucesso. As outras sessões foram encerradas.' });
    } catch (err) {
      setPwError(err.response?.data?.error?.message || 'Não foi possível alterar a senha.');
    } finally {
      setSavingPw(false);
    }
  };

  const formatDate = (iso) => iso ? new Date(iso).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' }) : '—';

  const sectionTitle = {
    fontSize: 13, fontWeight: 600, color: 'var(--nc-text-muted)',
    textTransform: 'uppercase', letterSpacing: 0.5, margin: '20px 0 8px'
  };

  return (
    <div className="nc-page nc-page--nav">
      <div className="nc-pagehead">
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
          <button onClick={() => navigate('/perfil')} aria-label="Voltar" className="nc-iconbtn">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M19 12H5" /><path d="M12 19l-7-7 7-7" />
            </svg>
          </button>
          <h1 className="nc-page-title">Minha conta</h1>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{
            width: 52, height: 52, borderRadius: '50%', overflow: 'hidden', flexShrink: 0,
            background: 'rgba(255,255,255,0.2)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 24
          }}>
            {user?.avatar ? (
              <img src={user.avatar} alt="Avatar" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            ) : '👤'}
          </div>
          <div>
            <div style={{ fontSize: 16, fontWeight: 600 }}>{user?.name}</div>
            <div style={{ fontSize: 13, opacity: 0.85 }}>{user?.email}</div>
            {selectedCity && (
              <div style={{ fontSize: 12, opacity: 0.75 }}>{selectedCity.name}/{selectedCity.state}</div>
            )}
          </div>
        </div>
      </div>

      <div className="nc-container" style={{ paddingTop: 8, paddingBottom: 8 }}>
        {message && (
          <div role="status" style={{
            padding: '10px 12px', borderRadius: 8, marginTop: 10, fontSize: 13,
            background: message.type === 'ok' ? '#f0fdf4' : '#fef2f2',
            border: `1px solid ${message.type === 'ok' ? '#bbf7d0' : '#fecaca'}`,
            color: message.type === 'ok' ? '#15803d' : '#b91c1c'
          }}>
            {message.text}
          </div>
        )}

        {/* Dados pessoais */}
        <div style={sectionTitle}>Seus dados</div>
        <div style={{ background: 'var(--nc-surface)', borderRadius: 12, border: '1px solid var(--nc-border)', padding: 14 }}>
          {/* LE-023: avatar de perfil — upload imediato + PATCH /auth/me */}
          <ImageUploadField
            label="Foto de perfil"
            hint="JPEG, PNG ou WebP até 5MB. Aparece no seu perfil e nas suas publicações."
            value={user?.avatar || ''}
            purpose="avatar"
            entityId={user?.id}
            onUploaded={async (url) => {
              setAvatarError(null);
              try {
                await updateAvatar(url);
                setMessage({ type: 'ok', text: 'Foto de perfil atualizada.' });
              } catch (err) {
                setAvatarError(err.response?.data?.error?.message || 'Não foi possível salvar a foto.');
              }
            }}
            onClear={async () => {
              setAvatarError(null);
              try {
                await updateAvatar('');
                setMessage({ type: 'ok', text: 'Foto de perfil removida.' });
              } catch (err) {
                setAvatarError(err.response?.data?.error?.message || 'Não foi possível remover a foto.');
              }
            }}
            round
          />
          {avatarError && <div role="alert" style={{ fontSize: 12, color: 'var(--nc-danger-text)', marginBottom: 10 }}>{avatarError}</div>}
          <Row label="Nome" value={user?.name} />
          <Row label="E-mail" value={user?.email} />
          <Row label="Telefone" value={user?.phone || '—'} />
          <Row label="Perfil" value={user?.role === 'admin' ? 'Administrador' : user?.role === 'merchant' ? 'Comerciante' : user?.role === 'delivery' ? 'Entregador' : 'Morador'} />
          {/* LE-027: edição de nome e telefone (PATCH /auth/me) */}
          {!editing ? (
            <button
              onClick={startEditing}
              style={{
                marginTop: 12, width: '100%', padding: '9px 12px', borderRadius: 8,
                background: 'var(--nc-primary)', color: 'var(--nc-on-primary)', border: 'none', fontSize: 13, fontWeight: 600, cursor: 'pointer'
              }}
            >
              ✏️ Editar nome e telefone
            </button>
          ) : (
            <form onSubmit={handleSaveProfile} style={{ marginTop: 12 }}>
              <label className="nc-hint" style={{ display: 'block', marginBottom: 4 }}>
                Nome
                <input
                  value={formName}
                  onChange={e => setFormName(e.target.value)}
                  maxLength={200}
                  aria-label="Nome"
                  style={inputStyle}
                />
              </label>
              <label className="nc-hint" style={{ display: 'block', marginBottom: 10 }}>
                Telefone (opcional)
                <input
                  value={formPhone}
                  onChange={e => setFormPhone(e.target.value)}
                  maxLength={20}
                  placeholder="(83) 99999-0000"
                  aria-label="Telefone"
                  style={inputStyle}
                />
              </label>
              <div style={{ display: 'flex', gap: 8 }}>
                <button type="submit" disabled={savingProfile} className="nc-btn-primary" style={{ flex: 1, opacity: savingProfile ? 0.7 : 1 }}>
                  {savingProfile ? 'Salvando...' : 'Salvar alterações'}
                </button>
                <button type="button" onClick={() => setEditing(false)} disabled={savingProfile} className="nc-btn-outline" style={{ flex: 1, opacity: savingProfile ? 0.6 : 1 }}>
                  Cancelar
                </button>
              </div>
            </form>
          )}
        </div>

        {/* LE-029: troca de senha */}
        <div style={sectionTitle}>Segurança</div>
        <div style={{ background: 'var(--nc-surface)', borderRadius: 12, border: '1px solid var(--nc-border)', padding: 14 }}>
          <div style={{ fontSize: 13, color: 'var(--nc-text)', fontWeight: 600, marginBottom: 4 }}>🔒 Alterar senha</div>
          <div className="nc-hint" style={{ lineHeight: 1.5, marginBottom: 10 }}>
            Ao trocar a senha, todas as outras sessões ativas são encerradas. Esta sessão permanece.
          </div>
          {pwError && <div role="alert" style={{ fontSize: 12, color: 'var(--nc-danger-text)', marginBottom: 10 }}>{pwError}</div>}
          {!pwEditing ? (
            <button
              onClick={startPwEditing}
              style={{
                width: '100%', padding: '9px 12px', borderRadius: 8,
                background: 'var(--nc-primary)', color: 'var(--nc-on-primary)', border: 'none', fontSize: 13, fontWeight: 600, cursor: 'pointer'
              }}
            >
              Trocar senha
            </button>
          ) : (
            <form onSubmit={handleChangePassword} style={{ marginTop: 4 }}>
              <label className="nc-hint" style={{ display: 'block', marginBottom: 10 }}>
                Senha atual
                <input
                  type="password"
                  value={pwCurrent}
                  onChange={e => setPwCurrent(e.target.value)}
                  autoComplete="current-password"
                  aria-label="Senha atual"
                  style={inputStyle}
                />
              </label>
              <label className="nc-hint" style={{ display: 'block', marginBottom: 10 }}>
                Nova senha (mínimo 8 caracteres)
                <input
                  type="password"
                  value={pwNew}
                  onChange={e => setPwNew(e.target.value)}
                  autoComplete="new-password"
                  aria-label="Nova senha"
                  style={inputStyle}
                />
              </label>
              <label className="nc-hint" style={{ display: 'block', marginBottom: 10 }}>
                Confirmar nova senha
                <input
                  type="password"
                  value={pwConfirm}
                  onChange={e => setPwConfirm(e.target.value)}
                  autoComplete="new-password"
                  aria-label="Confirmar nova senha"
                  style={inputStyle}
                />
              </label>
              <div style={{ display: 'flex', gap: 8 }}>
                <button type="submit" disabled={savingPw} className="nc-btn-primary" style={{ flex: 1, opacity: savingPw ? 0.7 : 1 }}>
                  {savingPw ? 'Salvando...' : 'Salvar nova senha'}
                </button>
                <button type="button" onClick={() => setPwEditing(false)} disabled={savingPw} className="nc-btn-outline" style={{ flex: 1, opacity: savingPw ? 0.6 : 1 }}>
                  Cancelar
                </button>
              </div>
            </form>
          )}
        </div>

        {/* LGPD */}
        <div style={sectionTitle}>Privacidade (LGPD)</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <button
            onClick={handleExport}
            disabled={busy}
            style={actionButton}
          >
            <span style={{ fontSize: 18 }}>📄</span> Exportar meus dados (JSON)
          </button>
          {exported && (
            <div className="nc-hint" style={{ padding: '0 4px' }}>
              Baixe o arquivo gerado. Você pode solicitar correção ou portabilidade pelo canal de contato.
            </div>
          )}
          <button
            onClick={() => { setConfirmDelete(!confirmDelete); setMessage(null); }}
            style={{ ...actionButton, background: 'var(--nc-danger-soft)', color: 'var(--nc-danger-text)', border: '1px solid var(--nc-danger-border)' }}
          >
            <span style={{ fontSize: 18 }}>🗑️</span> Excluir minha conta
          </button>
          {confirmDelete && (
            <div style={{
              background: 'var(--nc-danger-soft)', border: '1px solid var(--nc-danger-border)', borderRadius: 10, padding: 12, marginTop: 2
            }}>
              <div style={{ fontSize: 13, color: '#7f1d1d', lineHeight: 1.5, marginBottom: 10 }}>
                Sua conta será anonimizada e seus dados pessoais removidos. Avaliações e publicações feitas por você serão mantidas sem identificação, conforme exigido pela LGPD. Essa ação não pode ser desfeita.
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button onClick={handleDelete} disabled={busy} className="nc-btn-primary" style={{ flex: 1, background: '#dc2626' }}>
                  {busy ? 'Excluindo...' : 'Confirmar exclusão'}
                </button>
                <button onClick={() => setConfirmDelete(false)} className="nc-btn-outline" style={{ flex: 1 }}>
                  Cancelar
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Sessões */}
        <div style={{ ...sectionTitle, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          Sessões ativas
        </div>
        {loadingSessions ? <Loading message="Carregando sessões..." /> : (
          sessions.length === 0 ? (
            <EmptyState icon="🔐" title="Nenhuma sessão ativa" description="Suas sessões aparecerão aqui quando você fizer login." />
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {sessions.map(s => (
                <div key={s.id} style={{
                  display: 'flex', alignItems: 'center', gap: 10, padding: 12,
                  background: 'var(--nc-surface)', borderRadius: 10, border: '1px solid var(--nc-border)'
                }}>
                  <div style={{
                    width: 38, height: 38, borderRadius: 10, background: 'var(--nc-surface-alt)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 17, flexShrink: 0
                  }}>💻</div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--nc-text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {s.user_agent ? s.user_agent.slice(0, 48) : 'Dispositivo desconhecido'}
                    </div>
                    <div className="nc-caption">
                      Criada em {formatDate(s.created_at)}
                      {s.revoked_at ? ' • encerrada' : ` • expira ${formatDate(s.expires_at)}`}
                    </div>
                  </div>
                  {!s.revoked_at && (
                    <button
                      onClick={() => revokeSession(s.id)}
                      disabled={busy}
                      style={{
                        background: 'none', border: '1px solid var(--nc-danger-border)', color: 'var(--nc-danger-text)',
                        borderRadius: 8, padding: '6px 10px', fontSize: 12, fontWeight: 500, cursor: 'pointer'
                      }}
                    >
                      Encerrar
                    </button>
                  )}
                </div>
              ))}
            </div>
          )
        )}

        <button
          onClick={logout}
          style={{
            width: '100%', marginTop: 20, padding: 13, borderRadius: 10,
            background: 'var(--nc-surface)', color: 'var(--nc-danger-text)', border: '1px solid var(--nc-danger-border)',
            fontSize: 14, fontWeight: 600, cursor: 'pointer'
          }}
        >
          Sair em todos os dispositivos
        </button>
      </div>

      <BottomNav />
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid var(--nc-surface-alt)', fontSize: 13 }}>
      <span style={{ color: 'var(--nc-text-secondary)' }}>{label}</span>
      <span style={{ color: 'var(--nc-text)', fontWeight: 500, textAlign: 'right', wordBreak: 'break-all' }}>{value}</span>
    </div>
  );
}

const actionButton = {
  display: 'flex', alignItems: 'center', gap: 10, padding: '13px 14px',
  background: 'var(--nc-surface)', border: '1px solid var(--nc-border)', borderRadius: 10,
  fontSize: 14, fontWeight: 500, color: 'var(--nc-text)', cursor: 'pointer', width: '100%', textAlign: 'left'
};

const inputStyle = {
  width: '100%', padding: '9px 12px', borderRadius: 8, marginTop: 4,
  border: '1px solid var(--nc-border)', fontSize: 13,
  outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit',
};
